import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import ScrollArrow from '../components/ScrollArrow'
import GlareCard from '../components/ui/GlareCard'
import ProgressiveImage from '../components/ui/ProgressiveImage'
import { textReveal, staggerContainer, staggerItem } from '../animations/variants'

const Scenarios = () => {
  const { t } = useLanguage()

  return (
    <div className="bg-black">
      {/* Hero Section - Full Screen */}
      <section className="section-fullscreen">
        <div className="section-bg">
          <ProgressiveImage
            src="/images/hero-scenarios.jpg"
            placeholder="/images/placeholders/hero-scenarios-placeholder.jpg"
            alt="Automotive Factory"
          />
          <div className="section-overlay" />
          <div className="section-overlay-left" />
        </div>

        <div className="section-content">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.div variants={textReveal} className="section-label">SCENARIOS</motion.div>
            <motion.h1 variants={textReveal} className="section-heading text-glow">
              {t('products.usecases.title')}
            </motion.h1>
            <motion.p variants={textReveal} className="section-description">
              {t('products.usecases.subtitle')}
            </motion.p>
          </motion.div>
        </div>

        <ScrollArrow />
      </section>

      {/* Use Cases Section - 网格纹理背景 */}
      <section className="relative z-10 py-24 px-6 md:px-20 bg-grid-texture">
        {/* 顶部渐变过渡 */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black to-transparent pointer-events-none" />
        {/* 底部渐变过渡 */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent pointer-events-none" />

        <div className="relative max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'apartment',
                title: t('products.usecases.oem.title'),
                desc: t('products.usecases.oem.desc'),
                benefits: [
                  t('products.usecases.oem.benefits.1'),
                  t('products.usecases.oem.benefits.2'),
                  t('products.usecases.oem.benefits.3')
                ]
              },
              {
                icon: 'factory',
                title: t('products.usecases.tier1.title'),
                desc: t('products.usecases.tier1.desc'),
                benefits: [
                  t('products.usecases.tier1.benefits.1'),
                  t('products.usecases.tier1.benefits.2'),
                  t('products.usecases.tier1.benefits.3')
                ]
              },
              {
                icon: 'bolt',
                title: t('products.usecases.autonomous.title'),
                desc: t('products.usecases.autonomous.desc'),
                benefits: [
                  t('products.usecases.autonomous.benefits.1'),
                  t('products.usecases.autonomous.benefits.2'),
                  t('products.usecases.autonomous.benefits.3')
                ]
              }
            ].map((item, index) => (
              <GlareCard key={index}>
                <span className="material-symbols-outlined text-3xl text-white/30 mb-4">{item.icon}</span>
                <h3 className="section-title mb-4">{item.title}</h3>
                <p className="text-gray-500 text-sm font-mono mb-6">{item.desc}</p>
                <div className="space-y-3">
                  {item.benefits.map((benefit, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-white/30 text-sm">check_circle</span>
                      <span className="text-xs text-gray-400 font-mono">{benefit}</span>
                    </div>
                  ))}
                </div>
              </GlareCard>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Case Studies - 渐变纹理背景 */}
      <section className="relative z-10 py-24 px-6 md:px-20 bg-gradient-texture">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div variants={textReveal} className="section-label justify-center flex mb-6">CASE STUDIES</motion.div>
            <motion.h2 variants={textReveal} className="text-3xl md:text-4xl font-bold text-white mb-6 uppercase tracking-[-0.02em]">
              {t('scenarios.casestudies.title')}
            </motion.h2>
            <motion.p variants={textReveal} className="text-gray-400 font-mono text-sm max-w-3xl mx-auto">
              {t('scenarios.casestudies.subtitle')}
            </motion.p>
          </motion.div>

          <div className="space-y-24">
            {/* OEM Case Study */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial="hidden"
                whileInView="visible"
                variants={staggerContainer}
                viewport={{ once: true }}
              >
                <motion.div variants={textReveal} className="section-label mb-6">
                  <span className="material-symbols-outlined text-sm mr-2">apartment</span>
                  {t('scenarios.oem.badge')}
                </motion.div>
                <motion.h3 variants={textReveal} className="text-2xl font-bold text-white mb-4 uppercase tracking-[-0.02em]">
                  {t('scenarios.oem.case.title')}
                </motion.h3>
                <motion.p variants={textReveal} className="text-gray-400 font-mono text-sm mb-6">
                  {t('scenarios.oem.case.desc')}
                </motion.p>
                <motion.div variants={textReveal} className="grid grid-cols-2 gap-6">
                  <div className="p-4 border border-white/10">
                    <div className="text-2xl font-bold text-white mb-1 font-mono">30%</div>
                    <div className="text-xs text-gray-500 font-mono">{t('scenarios.oem.metrics.cycle')}</div>
                  </div>
                  <div className="p-4 border border-white/10">
                    <div className="text-2xl font-bold text-white mb-1 font-mono">12</div>
                    <div className="text-xs text-gray-500 font-mono">{t('scenarios.oem.metrics.issues')}</div>
                  </div>
                </motion.div>
              </motion.div>
              <motion.div
                initial="hidden"
                whileInView="visible"
                variants={textReveal}
                viewport={{ once: true }}
                className="metal-surface p-8"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-mono text-gray-500 uppercase tracking-wide">{t('scenarios.oem.progress.title')}</span>
                    <span className="text-sm font-mono text-white">85%</span>
                  </div>
                  <div className="w-full bg-white/10 h-1">
                    <div className="bg-white h-1" style={{ width: '85%' }}></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <div className="text-center p-4 border border-white/10">
                      <div className="text-lg font-bold text-white font-mono">15</div>
                      <div className="text-xs text-gray-500 font-mono">{t('scenarios.oem.progress.ecu')}</div>
                    </div>
                    <div className="text-center p-4 border border-white/10">
                      <div className="text-lg font-bold text-white font-mono">200+</div>
                      <div className="text-xs text-gray-500 font-mono">{t('scenarios.oem.progress.tests')}</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Tier1 Case Study */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial="hidden"
                whileInView="visible"
                variants={textReveal}
                viewport={{ once: true }}
                className="order-2 lg:order-1 metal-surface p-8"
              >
                <div className="space-y-4">
                  <div className="text-center mb-6">
                    <div className="text-3xl font-bold text-white mb-1 font-mono">80%</div>
                    <div className="text-sm text-gray-500 font-mono">{t('scenarios.tier1.metrics.efficiency')}</div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 font-mono">{t('scenarios.tier1.traditional')}</span>
                      <span className="text-sm font-mono text-gray-400">30天</span>
                    </div>
                    <div className="w-full bg-white/10 h-1">
                      <div className="bg-gray-600 h-1" style={{ width: '100%' }}></div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 font-mono">{t('scenarios.tier1.ai')}</span>
                      <span className="text-sm font-mono text-white">6天</span>
                    </div>
                    <div className="w-full bg-white/10 h-1">
                      <div className="bg-white h-1" style={{ width: '20%' }}></div>
                    </div>
                  </div>
                </div>
              </motion.div>
              <motion.div
                initial="hidden"
                whileInView="visible"
                variants={staggerContainer}
                viewport={{ once: true }}
                className="order-1 lg:order-2"
              >
                <motion.div variants={textReveal} className="section-label mb-6">
                  <span className="material-symbols-outlined text-sm mr-2">factory</span>
                  {t('scenarios.tier1.badge')}
                </motion.div>
                <motion.h3 variants={textReveal} className="text-2xl font-bold text-white mb-4 uppercase tracking-[-0.02em]">
                  {t('scenarios.tier1.case.title')}
                </motion.h3>
                <motion.p variants={textReveal} className="text-gray-400 font-mono text-sm mb-6">
                  {t('scenarios.tier1.case.desc')}
                </motion.p>
                <motion.div variants={staggerContainer} className="space-y-3">
                  {[
                    t('scenarios.tier1.benefits.routing'),
                    t('scenarios.tier1.benefits.comparison'),
                    t('scenarios.tier1.benefits.validation')
                  ].map((benefit, index) => (
                    <motion.div key={index} variants={staggerItem} className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-white/30 text-sm">check_circle</span>
                      <span className="text-gray-400 text-sm font-mono">{benefit}</span>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - 深灰背景 */}
      <section className="relative z-10 py-32 px-6 md:px-20 bg-dark-gray">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={staggerContainer}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center"
        >
          <motion.div variants={textReveal} className="section-label justify-center flex mb-6">GET STARTED</motion.div>
          <motion.h2 variants={textReveal} className="text-4xl md:text-5xl font-bold text-white mb-8 uppercase tracking-[-0.02em]">
            {t('scenarios.cta.title')}
          </motion.h2>
          <motion.p variants={textReveal} className="text-gray-500 text-lg font-mono mb-12">
            {t('scenarios.cta.subtitle')}
          </motion.p>
          <motion.div variants={textReveal} className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact" className="section-cta group">
              <span>{t('scenarios.cta.contact')}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/products" className="section-cta group border-white/10 hover:border-white/30 hover:bg-transparent hover:text-white">
              <span>{t('scenarios.cta.products')}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  )
}

export default Scenarios
